import { useNavigate } from 'react-router'
import { Icon } from './Icon'
import { Text } from './Text'

/**
 * Back-navigation sub-header — the row that sits directly below the app
 * <Header> on drill-in screens (Application Status, and future detail pages).
 *
 * A left chevron + a dynamic label; tapping anywhere on the row goes back. The
 * label is page-specific, so the same bar is reused everywhere by passing the
 * current page's title as `label` (typically a translated `t('…')` value).
 *
 * Navigation:
 * - Default: returns to the previous history entry (`navigate(-1)`), i.e. the
 *   page you came from — matching the design's "lead you back" behaviour.
 * - Pass `to` to go to a fixed route instead (useful when a screen can be
 *   reached by deep-link and there may be no history to pop).
 *
 * The chevron is the existing gray-700 menu icon, which already matches the
 * design's chevron geometry and color, so no new asset is introduced.
 */
export function BackBar({
  label,
  to,
}: {
  /** Visible text + accessible name for the row (the current page's title). */
  label: string
  /** Optional fixed destination; defaults to the previous history entry. */
  to?: string
}) {
  const navigate = useNavigate()

  return (
    <button
      type="button"
      onClick={() => (to ? navigate(to) : navigate(-1))}
      className="flex min-h-18 w-full items-center gap-4 bg-white px-6 text-left transition-colors active:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
    >
      <Icon name="chevron-left" />
      <Text variant="lg" weight="semibold" className="text-gray-900">
        {label}
      </Text>
    </button>
  )
}
